import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { contact_name: "", email: "", phone: "", event_date: "", guest_count: 100, venue_name: "", venue_address: "", notes: "", website: "" };

const inputCls = "w-full border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-500";

export default function EmbedForm() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/hub/${slug}`).then((r) => setData(r.data)).catch(() => setError("This enquiry form is unavailable right now."));
  }, [slug]);

  if (error) return <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6"><p data-testid="embed-error" className="border border-red-300 bg-red-50 px-6 py-4 text-sm text-red-700">{error}</p></div>;
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-gray-50"><Loader2 className="h-7 w-7 animate-spin text-gray-400" /></div>;

  const c = data.company;
  const accent = c.accent_color || "#059669";
  const dateBlocked = form.event_date && (data.blocked_dates || []).includes(form.event_date);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/public/hub/${slug}/enquiry`, { ...form, guest_count: Number(form.guest_count), menu_id: null });
      setDone(true);
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not send enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4" data-testid="embed-form-page">
      <div className="mx-auto max-w-xl bg-white p-6 shadow-sm" style={{ borderTop: `4px solid ${accent}` }}>
        {done ? (
          <div className="py-10 text-center" data-testid="embed-success">
            <CheckCircle2 className="mx-auto h-10 w-10" style={{ color: accent }} />
            <h1 className="mt-3 text-lg font-bold text-gray-900">Enquiry sent</h1>
            <p className="mt-1 text-sm text-gray-500">Thanks — {c.name} will be in touch shortly.</p>
          </div>
        ) : (
          <>
            <h1 className="text-lg font-bold text-gray-900" data-testid="embed-title">Enquire with {c.name}</h1>
            <p className="mt-1 text-sm text-gray-500">Tell us the date and numbers — we'll confirm availability and a tailored quote.</p>
            <form onSubmit={submit} className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="embed-enquiry-form">
              <input required placeholder="Your name" className={inputCls} data-testid="embed-name-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
              <input required type="email" placeholder="Email" className={inputCls} data-testid="embed-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input placeholder="Phone" className={inputCls} data-testid="embed-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <div>
                <input required type="date" min={new Date().toISOString().split("T")[0]} className={inputCls} data-testid="embed-date-input" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
                {dateBlocked && <p className="mt-1 text-xs font-bold text-red-600" data-testid="embed-date-blocked">Fully booked — pick another date.</p>}
              </div>
              <input required type="number" min="1" placeholder="Guests" className={inputCls} data-testid="embed-guests-input" value={form.guest_count} onChange={(e) => setForm({ ...form, guest_count: e.target.value })} />
              <input placeholder="Venue / location" className={inputCls} data-testid="embed-venue-input" value={form.venue_name} onChange={(e) => setForm({ ...form, venue_name: e.target.value })} />
              <textarea rows={2} placeholder="Anything else?" className={`${inputCls} col-span-1 sm:col-span-2`} data-testid="embed-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="hp-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
              <button type="submit" disabled={sending || dateBlocked} data-testid="embed-submit-button"
                className="col-span-1 sm:col-span-2 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-white disabled:opacity-50"
                style={{ backgroundColor: accent }}>
                <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send enquiry"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
